/**
 * "Line up by picture": for angles whose sound doesn't match the master's.
 * The creator puts both players on the same moment (a clap, a door closing,
 * someone sitting down); the gap between the two positions is how much later
 * this camera started than the master. From there it is placed exactly as a
 * sound match would place it (timingForOffset).
 */
import { AngleTiming, timingForOffset } from '../../utils/media/audioSync';

/** One frame at 30 fps: the players' smallest step. */
export const PICTURE_FRAME_MS = 1000 / 30;

/** "1:02.48": a position to 0.01 s, without ever reading "0:60.00". */
export const formatMoment = (ms: number): string => {
  const hundredths = Math.round(Math.max(0, Number.isFinite(ms) ? ms : 0) / 10);
  const minutes = Math.floor(hundredths / 6000);
  const seconds = (hundredths - minutes * 6000) / 100;
  return `${minutes}:${seconds.toFixed(2).padStart(5, '0')}`;
};

export interface PictureAngle {
  /** Where its footage starts on the master's timeline. */
  timelineStartMs: number;
  sourceInMs: number;
  sourceOutMs: number | null;
  /** The file's length; 0 when unknown. */
  durationMs: number;
}

export interface PictureLineUpPlan {
  /** How much later (negative: earlier) this camera started than the master. */
  offsetMs: number;
  timing: AngleTiming;
  /** Why these two moments can't be used, in words; null when they can. */
  problem: string | null;
}

/** The angle's start and trim when `masterMs` (into the master's file) and `angleMs` (into this one) show the same moment. */
export const planPictureLineUp = (
  masterMs: number,
  angleMs: number,
  masterSourceInMs: number,
  angle: PictureAngle
): PictureLineUpPlan => {
  const offsetMs = Math.round(masterMs - angleMs);
  const timing = timingForOffset(offsetMs / 1000, masterSourceInMs, angle.sourceInMs);
  const end = angle.sourceOutMs ?? (angle.durationMs > 0 ? angle.durationMs : null);
  const problem = end !== null && timing.sourceInMs >= end
    ? 'With these two moments, this camera’s footage would end before the master starts. Check that both players show the same moment.'
    : null;
  return { offsetMs, timing, problem };
};

/**
 * Where the two players open: the first moment this angle has footage, by
 * the way it is lined up now. Already lined up, they show the same picture.
 */
export const openingMoments = (
  master: { sourceInMs: number; durationMs: number },
  angle: PictureAngle
): { masterMs: number; angleMs: number } => {
  const timelineMs = Math.max(0, angle.timelineStartMs);
  const clamp = (ms: number, lengthMs: number) => Math.max(0, lengthMs > 0 ? Math.min(ms, lengthMs) : ms);
  return {
    masterMs: clamp(timelineMs + master.sourceInMs, master.durationMs),
    angleMs: clamp(timelineMs - angle.timelineStartMs + angle.sourceInMs, angle.durationMs),
  };
};

/** "12.48 s" */
const seconds = (ms: number): string => `${(Math.abs(ms) / 1000).toFixed(2)} s`;

/** When this camera started, as the sync step says it. */
export const describeStart = (offsetMs: number): string =>
  offsetMs >= 0
    ? `This camera started ${seconds(offsetMs)} after the master.`
    : `This camera started ${seconds(offsetMs)} before the master, so that much is trimmed from its start.`;
